/**
 * Error Dictionary - User-Friendly Error Messages with Recovery Actions
 *
 * UX-02: Improve Error Messages and Recovery
 *
 * This dictionary provides:
 * - User-friendly error messages (no technical jargon)
 * - Actionable recovery suggestions
 * - Error codes for support/debugging
 * - Categorization by domain
 *
 * Usage:
 * import { getErrorMessage, ERROR_CODES } from '@/lib/errorDictionary';
 * const error = getErrorMessage('AUTH_001');
 */

// ==========================================
// ERROR CODES BY CATEGORY
// ==========================================

export const ERROR_CODES = {
  // Authentication Errors (AUTH_xxx)
  AUTH_001: 'AUTH_001',
  AUTH_002: 'AUTH_002',
  AUTH_003: 'AUTH_003',
  AUTH_004: 'AUTH_004',
  AUTH_005: 'AUTH_005',
  AUTH_006: 'AUTH_006',
  AUTH_007: 'AUTH_007',

  // Network Errors (NET_xxx)
  NET_001: 'NET_001',
  NET_002: 'NET_002',
  NET_003: 'NET_003',
  NET_004: 'NET_004',
  NET_005: 'NET_005',

  // Payment Errors (PAY_xxx)
  PAY_001: 'PAY_001',
  PAY_002: 'PAY_002',
  PAY_003: 'PAY_003',
  PAY_004: 'PAY_004',
  PAY_005: 'PAY_005',

  // Invoice Errors (INV_xxx)
  INV_001: 'INV_001',
  INV_002: 'INV_002',
  INV_003: 'INV_003',
  INV_004: 'INV_004',
  INV_005: 'INV_005',
  INV_006: 'INV_006', // Duplicate invoice number
  INV_007: 'INV_007', // Invoice amount invalid
  INV_008: 'INV_008', // Invoice date invalid
  INV_009: 'INV_009', // Customer required
  INV_010: 'INV_010', // Email send failed
  INV_011: 'INV_011', // IBAN validation failed
  INV_012: 'INV_012', // Payment reconciliation mismatch

  // Database Errors (DB_xxx)
  DB_001: 'DB_001',
  DB_002: 'DB_002',
  DB_003: 'DB_003',

  // Validation Errors (VAL_xxx)
  VAL_001: 'VAL_001',
  VAL_002: 'VAL_002',
  VAL_003: 'VAL_003',

  // Permission Errors (PERM_xxx)
  PERM_001: 'PERM_001',
  PERM_002: 'PERM_002',
  PERM_003: 'PERM_003',

  // File Upload Errors (FILE_xxx)
  FILE_001: 'FILE_001',
  FILE_002: 'FILE_002',
  FILE_003: 'FILE_003',

  // Generic Errors (GEN_xxx)
  GEN_001: 'GEN_001',
  GEN_002: 'GEN_002',
  GEN_003: 'GEN_003',
};

// ==========================================
// ERROR MESSAGE DICTIONARY
// ==========================================

export const ERROR_MESSAGES = {
  // Authentication Errors
  [ERROR_CODES.AUTH_001]: {
    title: 'Login Failed',
    message: 'Your email or password is incorrect. Please check your credentials and try again.',
    severity: 'error',
    recoveryActions: [
      { label: 'Try Again', action: 'retry' },
      { label: 'Reset Password', action: 'reset-password', route: '/reset-password' },
      { label: 'Contact Support', action: 'support', route: '/contact' },
    ],
    suggestion: 'Double-check your email address and password. Passwords are case-sensitive.',
  },

  [ERROR_CODES.AUTH_002]: {
    title: 'Session Expired',
    message: 'Your session has expired for security. Please log in again to continue.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Log In Again', action: 'login', route: '/auth' },
    ],
    suggestion: 'We automatically log you out after 1 hour of inactivity to protect your account.',
  },

  [ERROR_CODES.AUTH_003]: {
    title: 'Email Not Verified',
    message: 'Please verify your email address before accessing this feature.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Resend Verification Email', action: 'resend-verification' },
      { label: 'Update Email Address', action: 'update-email', route: '/settings/email' },
    ],
    suggestion: 'Check your inbox (and spam folder) for the verification email we sent you.',
  },

  [ERROR_CODES.AUTH_004]: {
    title: 'Account Locked',
    message: 'Your account has been temporarily locked due to multiple failed login attempts.',
    severity: 'error',
    recoveryActions: [
      { label: 'Reset Password', action: 'reset-password', route: '/reset-password' },
      { label: 'Contact Support', action: 'support', route: '/contact' },
    ],
    suggestion: 'For security, accounts are locked for 30 minutes after 5 failed login attempts.',
  },

  [ERROR_CODES.AUTH_005]: {
    title: 'Invalid Token',
    message: 'The authentication token is invalid or has expired.',
    severity: 'error',
    recoveryActions: [
      { label: 'Log In Again', action: 'login', route: '/auth' },
    ],
    suggestion: 'This usually happens when you use an old or invalid link. Please log in again.',
  },

  [ERROR_CODES.AUTH_006]: {
    title: 'Two-Factor Authentication Required',
    message: 'Please enter your two-factor authentication code to continue.',
    severity: 'info',
    recoveryActions: [
      { label: 'Enter 2FA Code', action: 'show-2fa' },
      { label: 'Use Backup Code', action: 'use-backup-code' },
    ],
    suggestion: 'Check your authenticator app for the 6-digit code.',
  },

  [ERROR_CODES.AUTH_007]: {
    title: 'Account Not Found',
    message: 'No account exists with this email address.',
    severity: 'error',
    recoveryActions: [
      { label: 'Create Account', action: 'signup', route: '/signup' },
      { label: 'Try Different Email', action: 'retry' },
    ],
    suggestion: 'Check the email address for typos, or create a new account.',
  },

  // Network Errors
  [ERROR_CODES.NET_001]: {
    title: 'Connection Lost',
    message: 'Unable to connect to the server. Please check your internet connection.',
    severity: 'error',
    recoveryActions: [
      { label: 'Retry', action: 'retry' },
      { label: 'Check Connection', action: 'check-connection' },
    ],
    suggestion: 'Make sure you\'re connected to the internet and try again.',
  },

  [ERROR_CODES.NET_002]: {
    title: 'Request Timeout',
    message: 'The request took too long to complete. This might be due to slow connection.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Try Again', action: 'retry' },
      { label: 'Check Network Speed', action: 'check-network' },
    ],
    suggestion: 'If you\'re on a slow connection, try again when you have better connectivity.',
  },

  [ERROR_CODES.NET_003]: {
    title: 'Server Unavailable',
    message: 'Our servers are currently unavailable. We\'re working to restore service.',
    severity: 'error',
    recoveryActions: [
      { label: 'Retry in 1 Minute', action: 'retry-delayed', delay: 60000 },
      { label: 'Check Status Page', action: 'open-status', route: '/status' },
    ],
    suggestion: 'Check our status page for updates on service restoration.',
  },

  [ERROR_CODES.NET_004]: {
    title: 'Rate Limit Exceeded',
    message: 'You\'ve made too many requests. Please wait a moment and try again.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Wait 30 Seconds', action: 'wait', delay: 30000 },
    ],
    suggestion: 'To prevent abuse, we limit the number of requests per minute.',
  },

  [ERROR_CODES.NET_005]: {
    title: 'Network Error',
    message: 'A network error occurred. Please check your connection and try again.',
    severity: 'error',
    recoveryActions: [
      { label: 'Retry', action: 'retry' },
      { label: 'Refresh Page', action: 'refresh' },
    ],
    suggestion: 'This is usually a temporary issue. Refreshing the page often helps.',
  },

  // Payment Errors
  [ERROR_CODES.PAY_001]: {
    title: 'Payment Failed',
    message: 'Your payment could not be processed. Please check your payment method.',
    severity: 'error',
    recoveryActions: [
      { label: 'Try Again', action: 'retry-payment' },
      { label: 'Update Payment Method', action: 'update-payment', route: '/settings/billing' },
      { label: 'Contact Support', action: 'support', route: '/contact' },
    ],
    suggestion: 'Make sure your card details are correct and you have sufficient funds.',
  },

  [ERROR_CODES.PAY_002]: {
    title: 'Invalid Payment Method',
    message: 'The payment method you selected is invalid or expired.',
    severity: 'error',
    recoveryActions: [
      { label: 'Add New Payment Method', action: 'add-payment', route: '/settings/billing' },
      { label: 'Update Existing Method', action: 'update-payment', route: '/settings/billing' },
    ],
    suggestion: 'Check the expiry date and billing address of your payment method.',
  },

  [ERROR_CODES.PAY_003]: {
    title: 'Insufficient Funds',
    message: 'Your payment was declined due to insufficient funds.',
    severity: 'error',
    recoveryActions: [
      { label: 'Use Different Card', action: 'change-payment', route: '/settings/billing' },
      { label: 'Contact Your Bank', action: 'contact-bank' },
    ],
    suggestion: 'Contact your bank or try a different payment method.',
  },

  [ERROR_CODES.PAY_004]: {
    title: 'Payment Processing',
    message: 'Your payment is being processed. This may take a few moments.',
    severity: 'info',
    recoveryActions: [
      { label: 'Check Payment Status', action: 'check-status', route: '/payments' },
    ],
    suggestion: 'You\'ll receive a confirmation email once the payment is complete.',
  },

  [ERROR_CODES.PAY_005]: {
    title: '3D Secure Verification Required',
    message: 'Your bank requires additional verification for this payment.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Complete Verification', action: 'verify-3ds' },
    ],
    suggestion: 'You\'ll be redirected to your bank\'s verification page.',
  },

  // Invoice Errors
  [ERROR_CODES.INV_001]: {
    title: 'Invoice Not Found',
    message: 'The invoice you\'re looking for doesn\'t exist or has been deleted.',
    severity: 'error',
    recoveryActions: [
      { label: 'View All Invoices', action: 'view-invoices', route: '/invoices' },
      { label: 'Create New Invoice', action: 'create-invoice', route: '/invoices/new' },
    ],
    suggestion: 'Check the invoice number and try again.',
  },

  [ERROR_CODES.INV_002]: {
    title: 'Invalid Invoice Data',
    message: 'Some required information is missing from the invoice.',
    severity: 'error',
    recoveryActions: [
      { label: 'Review Invoice', action: 'review-invoice' },
      { label: 'Edit Invoice', action: 'edit-invoice' },
    ],
    suggestion: 'Make sure all required fields are filled in correctly.',
  },

  [ERROR_CODES.INV_003]: {
    title: 'PDF Generation Failed',
    message: 'Unable to generate the invoice PDF. Please try again.',
    severity: 'error',
    recoveryActions: [
      { label: 'Try Again', action: 'retry' },
      { label: 'Download as CSV', action: 'download-csv' },
      { label: 'Contact Support', action: 'support', route: '/contact' },
    ],
    suggestion: 'If the problem persists, try downloading in a different format.',
  },

  [ERROR_CODES.INV_004]: {
    title: 'Cannot Modify Paid Invoice',
    message: 'This invoice has already been paid and cannot be modified.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Create Credit Note', action: 'create-credit', route: '/invoices/credit' },
      { label: 'Duplicate Invoice', action: 'duplicate-invoice' },
    ],
    suggestion: 'To make changes, create a credit note or a new invoice.',
  },

  [ERROR_CODES.INV_005]: {
    title: 'Invalid QR-Bill Data',
    message: 'The Swiss QR-Bill data is incomplete or invalid.',
    severity: 'error',
    recoveryActions: [
      { label: 'Review QR-Bill Settings', action: 'review-qr', route: '/settings/qr-bill' },
      { label: 'Edit Invoice', action: 'edit-invoice' },
    ],
    suggestion: 'Make sure the IBAN and reference number are correct.',
  },

  [ERROR_CODES.INV_006]: {
    title: 'Duplicate Invoice Number',
    message: 'An invoice with this number already exists.',
    severity: 'warning',
    recoveryActions: [
      { label: 'View Existing Invoice', action: 'view-existing' },
      { label: 'Use Different Number', action: 'change-number' },
    ],
    suggestion: 'Invoice numbers must be unique. Try using the next available number.',
  },

  [ERROR_CODES.INV_007]: {
    title: 'Invalid Amount',
    message: 'The invoice amount must be a positive number greater than zero.',
    severity: 'error',
    recoveryActions: [
      { label: 'Correct Amount', action: 'edit-amount' },
    ],
    suggestion: 'Enter a valid amount between CHF 0.01 and CHF 999,999,999.99.',
  },

  [ERROR_CODES.INV_008]: {
    title: 'Invalid Invoice Date',
    message: 'The due date cannot be before the invoice date.',
    severity: 'error',
    recoveryActions: [
      { label: 'Correct Dates', action: 'edit-dates' },
    ],
    suggestion: 'Set the due date to be on or after the invoice date.',
  },

  [ERROR_CODES.INV_009]: {
    title: 'Customer Required',
    message: 'Please select a customer before creating the invoice.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Select Customer', action: 'select-customer' },
      { label: 'Create New Customer', action: 'create-customer', route: '/customers/new' },
    ],
    suggestion: 'Choose an existing customer or create a new one to proceed.',
  },

  [ERROR_CODES.INV_010]: {
    title: 'Email Send Failed',
    message: 'The invoice could not be sent to the customer\'s email.',
    severity: 'error',
    recoveryActions: [
      { label: 'Retry Send', action: 'retry-send' },
      { label: 'Check Email Address', action: 'edit-email' },
      { label: 'Download PDF', action: 'download-pdf' },
    ],
    suggestion: 'Verify the customer\'s email address is correct, or download the PDF to send manually.',
  },

  [ERROR_CODES.INV_011]: {
    title: 'Invalid IBAN',
    message: 'The IBAN provided is invalid or not in the correct format.',
    severity: 'error',
    recoveryActions: [
      { label: 'Update Banking Details', action: 'update-banking', route: '/settings/banking' },
    ],
    suggestion: 'Swiss IBANs must start with CH or LI and contain 21 characters (e.g., CH93 0076 2011 6238 5295 7).',
  },

  [ERROR_CODES.INV_012]: {
    title: 'Payment Reconciliation Mismatch',
    message: 'The payment amount doesn\'t match the invoice total.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Review Payment', action: 'review-payment' },
      { label: 'Mark as Partial', action: 'mark-partial' },
      { label: 'Create Adjustment', action: 'create-adjustment' },
    ],
    suggestion: 'The received payment differs from the invoice amount. You can mark it as partial or create an adjustment.',
  },

  // Database Errors
  [ERROR_CODES.DB_001]: {
    title: 'Save Failed',
    message: 'Unable to save your changes. Please try again.',
    severity: 'error',
    recoveryActions: [
      { label: 'Try Again', action: 'retry' },
      { label: 'Save Draft Locally', action: 'save-local' },
    ],
    suggestion: 'Your data is temporarily cached. Try saving again in a moment.',
  },

  [ERROR_CODES.DB_002]: {
    title: 'Delete Failed',
    message: 'Unable to delete this item. It may be in use.',
    severity: 'error',
    recoveryActions: [
      { label: 'Try Again', action: 'retry' },
      { label: 'Archive Instead', action: 'archive' },
    ],
    suggestion: 'This item might be referenced by other records. Consider archiving instead.',
  },

  [ERROR_CODES.DB_003]: {
    title: 'Duplicate Entry',
    message: 'A record with this information already exists.',
    severity: 'warning',
    recoveryActions: [
      { label: 'View Existing', action: 'view-existing' },
      { label: 'Update Existing', action: 'update-existing' },
      { label: 'Use Different Value', action: 'change-value' },
    ],
    suggestion: 'Check if a similar record already exists before creating a new one.',
  },

  // Validation Errors
  [ERROR_CODES.VAL_001]: {
    title: 'Required Fields Missing',
    message: 'Please fill in all required fields before continuing.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Review Form', action: 'review-form' },
    ],
    suggestion: 'Fields marked with * are required.',
  },

  [ERROR_CODES.VAL_002]: {
    title: 'Invalid Format',
    message: 'Some fields contain invalid data. Please check and correct them.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Review Errors', action: 'review-errors' },
    ],
    suggestion: 'Look for red error messages next to each field.',
  },

  [ERROR_CODES.VAL_003]: {
    title: 'Data Out of Range',
    message: 'One or more values are outside the acceptable range.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Review Values', action: 'review-values' },
    ],
    suggestion: 'Check that numbers, dates, and amounts are within allowed limits.',
  },

  // Permission Errors
  [ERROR_CODES.PERM_001]: {
    title: 'Access Denied',
    message: 'You don\'t have permission to access this resource.',
    severity: 'error',
    recoveryActions: [
      { label: 'Request Access', action: 'request-access' },
      { label: 'Contact Admin', action: 'contact-admin', route: '/contact' },
      { label: 'Go Back', action: 'go-back' },
    ],
    suggestion: 'Contact your administrator to request the necessary permissions.',
  },

  [ERROR_CODES.PERM_002]: {
    title: 'Read-Only Access',
    message: 'You can view this resource but cannot make changes.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Request Edit Access', action: 'request-edit' },
      { label: 'View Only', action: 'continue-readonly' },
    ],
    suggestion: 'Your current role has read-only access to this feature.',
  },

  [ERROR_CODES.PERM_003]: {
    title: 'Tenant Mismatch',
    message: 'This resource belongs to a different organization.',
    severity: 'error',
    recoveryActions: [
      { label: 'Switch Organization', action: 'switch-tenant', route: '/organizations' },
      { label: 'Go to Dashboard', action: 'dashboard', route: '/dashboard' },
    ],
    suggestion: 'Make sure you\'re logged into the correct organization.',
  },

  // File Upload Errors
  [ERROR_CODES.FILE_001]: {
    title: 'File Too Large',
    message: 'The file you\'re trying to upload exceeds the maximum size limit.',
    severity: 'error',
    recoveryActions: [
      { label: 'Choose Smaller File', action: 'choose-file' },
      { label: 'Compress File', action: 'compress' },
    ],
    suggestion: 'Maximum file size is 10MB. Try compressing the file or uploading a smaller version.',
  },

  [ERROR_CODES.FILE_002]: {
    title: 'Invalid File Type',
    message: 'This file type is not supported. Please upload a different format.',
    severity: 'error',
    recoveryActions: [
      { label: 'Choose Different File', action: 'choose-file' },
      { label: 'View Supported Types', action: 'view-types' },
    ],
    suggestion: 'Supported formats: PDF, JPG, PNG, CSV, XLSX',
  },

  [ERROR_CODES.FILE_003]: {
    title: 'Upload Failed',
    message: 'Unable to upload the file. Please try again.',
    severity: 'error',
    recoveryActions: [
      { label: 'Try Again', action: 'retry' },
      { label: 'Check Connection', action: 'check-connection' },
    ],
    suggestion: 'Make sure you have a stable internet connection and the file isn\'t corrupted.',
  },

  // Generic Errors
  [ERROR_CODES.GEN_001]: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred. We\'ve been notified and are looking into it.',
    severity: 'error',
    recoveryActions: [
      { label: 'Try Again', action: 'retry' },
      { label: 'Refresh Page', action: 'refresh' },
      { label: 'Contact Support', action: 'support', route: '/contact' },
    ],
    suggestion: 'This is usually a temporary issue. Please try again in a moment.',
  },

  [ERROR_CODES.GEN_002]: {
    title: 'Feature Unavailable',
    message: 'This feature is temporarily unavailable. Please try again later.',
    severity: 'warning',
    recoveryActions: [
      { label: 'Go Back', action: 'go-back' },
      { label: 'Check Status', action: 'check-status', route: '/status' },
    ],
    suggestion: 'We\'re working to restore this feature. Check back soon.',
  },

  [ERROR_CODES.GEN_003]: {
    title: 'Maintenance Mode',
    message: 'We\'re currently performing scheduled maintenance. Service will resume shortly.',
    severity: 'info',
    recoveryActions: [
      { label: 'Check Status', action: 'check-status', route: '/status' },
    ],
    suggestion: 'Maintenance typically takes 10-15 minutes. Thank you for your patience.',
  },
};

// ==========================================
// ERROR PATTERN MATCHING
// ==========================================

/**
 * Match common error patterns to error codes
 */
const ERROR_PATTERNS = [
  // Authentication
  { pattern: /invalid.*credentials|wrong.*password|incorrect.*email/i, code: ERROR_CODES.AUTH_001 },
  { pattern: /session.*expired|token.*expired/i, code: ERROR_CODES.AUTH_002 },
  { pattern: /email.*not.*verified|unverified.*email/i, code: ERROR_CODES.AUTH_003 },
  { pattern: /account.*locked|too.*many.*attempts/i, code: ERROR_CODES.AUTH_004 },
  { pattern: /invalid.*token|token.*invalid/i, code: ERROR_CODES.AUTH_005 },
  { pattern: /2fa|two.*factor|authentication.*code/i, code: ERROR_CODES.AUTH_006 },
  { pattern: /user.*not.*found|account.*not.*found/i, code: ERROR_CODES.AUTH_007 },

  // Network
  { pattern: /network.*error|connection.*failed|failed.*to.*fetch/i, code: ERROR_CODES.NET_001 },
  { pattern: /timeout|timed.*out/i, code: ERROR_CODES.NET_002 },
  { pattern: /server.*unavailable|503|502|500/i, code: ERROR_CODES.NET_003 },
  { pattern: /rate.*limit|too.*many.*requests|429/i, code: ERROR_CODES.NET_004 },

  // Payment
  { pattern: /payment.*failed|transaction.*failed/i, code: ERROR_CODES.PAY_001 },
  { pattern: /invalid.*card|expired.*card|card.*declined/i, code: ERROR_CODES.PAY_002 },
  { pattern: /insufficient.*funds|insufficient.*balance/i, code: ERROR_CODES.PAY_003 },
  { pattern: /3d.*secure|3ds/i, code: ERROR_CODES.PAY_005 },

  // Invoice
  { pattern: /invoice.*not.*found|404.*invoice/i, code: ERROR_CODES.INV_001 },
  { pattern: /pdf.*generation|pdf.*failed/i, code: ERROR_CODES.INV_003 },
  { pattern: /cannot.*modify.*paid|paid.*invoice/i, code: ERROR_CODES.INV_004 },
  { pattern: /qr.*bill|qr.*code.*invalid/i, code: ERROR_CODES.INV_005 },
  { pattern: /duplicate.*invoice.*number|invoice.*number.*exists/i, code: ERROR_CODES.INV_006 },
  { pattern: /invalid.*amount|amount.*invalid|amount.*zero|negative.*amount/i, code: ERROR_CODES.INV_007 },
  { pattern: /invalid.*date|due.*date.*before|date.*invalid/i, code: ERROR_CODES.INV_008 },
  { pattern: /customer.*required|select.*customer|no.*customer/i, code: ERROR_CODES.INV_009 },
  { pattern: /email.*send.*failed|failed.*send.*email|invoice.*email/i, code: ERROR_CODES.INV_010 },
  { pattern: /invalid.*iban|iban.*invalid|iban.*format/i, code: ERROR_CODES.INV_011 },
  { pattern: /payment.*mismatch|reconciliation|amount.*differ/i, code: ERROR_CODES.INV_012 },

  // Database
  { pattern: /save.*failed|failed.*to.*save/i, code: ERROR_CODES.DB_001 },
  { pattern: /delete.*failed|failed.*to.*delete/i, code: ERROR_CODES.DB_002 },
  { pattern: /duplicate.*entry|already.*exists/i, code: ERROR_CODES.DB_003 },

  // Validation
  { pattern: /required.*field|field.*required/i, code: ERROR_CODES.VAL_001 },
  { pattern: /invalid.*format|format.*invalid/i, code: ERROR_CODES.VAL_002 },

  // Permission
  { pattern: /access.*denied|forbidden|403|unauthorized|401/i, code: ERROR_CODES.PERM_001 },
  { pattern: /read.*only|cannot.*edit/i, code: ERROR_CODES.PERM_002 },
  { pattern: /tenant.*mismatch|wrong.*organization/i, code: ERROR_CODES.PERM_003 },

  // File Upload
  { pattern: /file.*too.*large|size.*exceeded|file.*size/i, code: ERROR_CODES.FILE_001 },
  { pattern: /invalid.*file.*type|unsupported.*format/i, code: ERROR_CODES.FILE_002 },
  { pattern: /upload.*failed|failed.*to.*upload/i, code: ERROR_CODES.FILE_003 },
];

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Get error message details from error code or pattern matching
 * @param {string|Error} errorCodeOrMessage - Error code or error object/message
 * @param {Object} context - Additional context for error customization
 * @returns {Object} Error message details
 */
export function getErrorMessage(errorCodeOrMessage, context = {}) {
  let errorCode = errorCodeOrMessage;

  // Handle Error objects
  if (errorCodeOrMessage instanceof Error) {
    errorCode = matchErrorPattern(errorCodeOrMessage.message);
  }
  // Handle string messages
  else if (typeof errorCodeOrMessage === 'string' && !ERROR_MESSAGES[errorCodeOrMessage]) {
    errorCode = matchErrorPattern(errorCodeOrMessage);
  }

  // Get error details or fallback to generic
  const errorDetails = ERROR_MESSAGES[errorCode] || ERROR_MESSAGES[ERROR_CODES.GEN_001];

  // Merge context if provided
  return {
    code: errorCode,
    ...errorDetails,
    context,
  };
}

/**
 * Match error message against patterns to determine error code
 * @param {string} message - Error message to match
 * @returns {string} Matched error code or generic code
 */
export function matchErrorPattern(message) {
  if (!message) return ERROR_CODES.GEN_001;

  for (const { pattern, code } of ERROR_PATTERNS) {
    if (pattern.test(message)) {
      return code;
    }
  }

  return ERROR_CODES.GEN_001;
}

/**
 * Format error for user display
 * @param {string|Error} error - Error to format
 * @param {Object} context - Additional context
 * @returns {Object} Formatted error object
 */
export function formatError(error, context = {}) {
  const errorMessage = getErrorMessage(error, context);

  return {
    ...errorMessage,
    timestamp: new Date().toISOString(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  };
}

/**
 * Get recovery action handler
 * @param {string} action - Action type
 * @param {Object} params - Action parameters
 * @returns {Function} Action handler
 */
export function getRecoveryActionHandler(action, params = {}) {
  const handlers = {
    retry: () => {
      if (params.retryFn) {
        params.retryFn();
      }
    },
    refresh: () => {
      window.location.reload();
    },
    'go-back': () => {
      window.history.back();
    },
    'reset-password': () => {
      if (params.navigate) {
        params.navigate('/reset-password');
      }
    },
    login: () => {
      if (params.navigate) {
        params.navigate('/auth');
      }
    },
    support: () => {
      if (params.navigate) {
        params.navigate('/contact');
      }
    },
    dashboard: () => {
      if (params.navigate) {
        params.navigate('/dashboard');
      }
    },
  };

  return handlers[action] || (() => console.warn(`Unknown recovery action: ${action}`));
}

/**
 * Check if error is recoverable
 * @param {string} errorCode - Error code
 * @returns {boolean} True if error has recovery actions
 */
export function isRecoverable(errorCode) {
  const error = ERROR_MESSAGES[errorCode];
  return error && error.recoveryActions && error.recoveryActions.length > 0;
}

export default {
  ERROR_CODES,
  ERROR_MESSAGES,
  getErrorMessage,
  matchErrorPattern,
  formatError,
  getRecoveryActionHandler,
  isRecoverable,
};
